import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Alert,
} from "reactstrap";
import * as Yup from "yup";

import {
  CreateSystemAdminRequest,
  ParsedError,
} from "../../../interfaces";
import { ActivityButton } from "../../../components/Common";
import { EducationSystemsSelect } from "../../../components/Selects";
import { ErrorHandlingTypeEnum, GenderEnum } from "../../../enums";
import { InputType } from "reactstrap/types/lib/Input";
// @ts-ignore
import "react-phone-number-input/style.css";
import PhoneInput, { PhoneNumber } from "react-phone-number-input";
// @ts-ignore
import ar from "react-phone-number-input/locale/ar";
// @ts-ignore
import en from "react-phone-number-input/locale/en";
import parsePhoneNumberFromString from "libphonenumber-js";
import {
  useGetSystemAdminUserQuery,
  useAddSystemAdminUserMutation,
  useUpdateSystemAdminUserMutation,
} from "../../../store/api/systemAdminApi";
import { SelectListDto } from "../../../models";

type EducationSystemOption = { value: string; label: string };

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditSystemAdminModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
}) => {
  const { t, i18n } = useTranslation();

  const [request] = useState<CreateSystemAdminRequest>();
  const [passwordType, setPasswordType] = useState<InputType>("password");
  const [selectedEducationSystems, setSelectedEducationSystems] = useState<
    EducationSystemOption[]
  >([]);

  const {
    data: dataSystemAdmin,
    isLoading: isLoadingSystemAdmin,
    error: errorSystemAdmin,
  } = useGetSystemAdminUserQuery(id as string, { skip: !id });

  const [
    addSystemAdminUser,
    { isLoading: isLoadingAdd, error: errorAdd, isError: isErrorAdd },
  ] = useAddSystemAdminUserMutation();

  const [
    updateSystemAdminUser,
    { isLoading: isLoadingUpdate, error: errorUpdate, isError: isErrorUpdate },
  ] = useUpdateSystemAdminUserMutation();

  const form = useFormik<CreateSystemAdminRequest>({
    enableReinitialize: true,
    initialValues: {
      id: request?.id,
      name: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
      countryCode:
        request?.countryCode || process.env.REACT_APP_COUNTRY_CODE || "965",
      gender: request?.gender || GenderEnum.MALE,
      password: "",
      phoneNumber: request?.phoneNumber || "",
      secondaryPhoneCountryCode:
        request?.secondaryPhoneCountryCode ||
        process.env.REACT_APP_COUNTRY_CODE ||
        "965",
      secondaryPhonePhoneNumber: request?.secondaryPhonePhoneNumber || "",
      educationSystemIds: request?.educationSystemIds || [],
    },
    validationSchema: Yup.object({
      id: Yup.string().optional(),
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      phoneNumber: Yup.string().required(
        t("isNotEmpty", {
          displayName: "phoneNumber",
        }),
      ),
      password: Yup.string()
        .min(8, t("minLength", { displayName: "password", minLength: 8 }))
        .max(25, t("maxLength", { displayName: "password", maxLength: 25 }))
        .when("id", (id: any): any => {
          if (id == "" || id == null || id == undefined)
            return Yup.string()
              .min(
                8,
                t("minLength", {
                  displayName: "password",
                  minLength: 8,
                }),
              )
              .max(
                25,
                t("maxLength", {
                  displayName: "password",
                  maxLength: 25,
                }),
              )
              .required(
                t("isNotEmpty", {
                  displayName: "password",
                }),
              );
        }),
      secondaryPhoneCountryCode: Yup.string().nullable(),
      secondaryPhonePhoneNumber: Yup.string().nullable(),
      educationSystemIds: Yup.array()
        .of(Yup.string())
        .min(1, t("chooseAtLeast1EducationSystem")),
    }),
    onSubmit: async (values) => {
      if (!selectedEducationSystems.length) {
        form.setFieldError(
          "educationSystemIds",
          t("chooseAtLeast1EducationSystem"),
        );
        return;
      }

      let formedRequest = {
        ...values,
        educationSystemIds: selectedEducationSystems.map((s) => s.value),
      };
      if (values.secondaryPhonePhoneNumber) {
        const parsePhoneNumber: PhoneNumber | undefined =
          parsePhoneNumberFromString(values.secondaryPhonePhoneNumber);
        if (!parsePhoneNumber) {
          form.setFieldError(
            "secondaryPhonePhoneNumber",
            t("Invalid Phone Number"),
          );
          return;
        }
        formedRequest = {
          ...formedRequest,
          secondaryPhoneCountryCode: parsePhoneNumber.countryCallingCode,
          secondaryPhonePhoneNumber: parsePhoneNumber.nationalNumber as string,
        };
      }

      try {
        if (id) {
          await updateSystemAdminUser({
            id,
            data: formedRequest,
          }).unwrap();
        } else {
          await addSystemAdminUser(formedRequest).unwrap();
        }
        onClose();
      } catch (err) {}
    },
  });

  useEffect(() => {
    if (id && dataSystemAdmin) {
      form.setValues({
        id: id,
        name: dataSystemAdmin.name,
        countryCode: dataSystemAdmin.countryCode,
        phoneNumber: dataSystemAdmin.phone,
        gender: dataSystemAdmin.gender,
        secondaryPhoneCountryCode:
          dataSystemAdmin.secondaryPhoneCountryCode,
        secondaryPhonePhoneNumber:
          dataSystemAdmin.secondaryPhoneCountryCode &&
          dataSystemAdmin.secondaryPhonePhoneNumber
            ? `+${dataSystemAdmin.secondaryPhoneCountryCode}${dataSystemAdmin.secondaryPhonePhoneNumber}`
            : "",
        educationSystemIds: (dataSystemAdmin.educationSystems || []).map(
          (item: SelectListDto) => item.id,
        ),
      });
      setSelectedEducationSystems(
        (dataSystemAdmin.educationSystems || []).map(
          (item: SelectListDto) => ({
            value: item.id,
            label: i18n.language === "ar" ? item.value.ar : item.value.en,
          }),
        ),
      );
    }
  }, [dataSystemAdmin, isLoadingSystemAdmin, id, i18n.language]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  const onClose = () => {
    form.resetForm();
    setSelectedEducationSystems([]);
    onCloseClick(!show);
  };

  const handleClickShowPassword = () => {
    if (passwordType === "text") setPasswordType("password");
    else setPasswordType("text");
  };

  const onChangeEducationSystems = (changedValue: any) => {
    const next = changedValue || [];
    setSelectedEducationSystems(next);
    form.setFieldValue(
      "educationSystemIds",
      next.map((item: EducationSystemOption) => item.value),
    );
    form.setFieldTouched("educationSystemIds", true);
  };

  return (
    <React.Fragment>
      <Modal
        id="addEditSystemAdmin"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        size="xl"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "user" })
              : t("addItem", { item: "user" })}
          </ModalHeader>
          <ModalBody>
            <Alert color="info">
              {t("roles.systemAdmin.title")}
              <ul>
                <li>{t("roles.systemAdmin.line1")}</li>
                <li>{t("roles.systemAdmin.line2")}</li>
                <li>{t("roles.systemAdmin.line3")}</li>
              </ul>
            </Alert>
            <div className="mb-3">
              <label htmlFor="name.en" className="form-label">
                {t("nameEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={!!errorSystemAdmin}
                autoComplete="off"
                id="name.en"
                name="name.en"
                value={form.values?.name.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.en && form.errors.name?.en ? true : false
                }
              />
              {form.touched.name?.en && form.errors.name?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="name.ar" className="form-label">
                {t("nameAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                disabled={!!errorSystemAdmin}
                autoComplete="off"
                id="name.ar"
                name="name.ar"
                value={form.values?.name.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.ar && form.errors.name?.ar ? true : false
                }
              />
              {form.touched.name?.ar && form.errors.name?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <Label htmlFor="phoneNumber" className="form-label">
                {t("primaryPhoneNumber")}
                <span className="text-danger">*</span>
                <span className="text-info">&nbsp;({t("useForLogin")})</span>
              </Label>

              <Input
                disabled={!!id}
                autoComplete="off"
                autoCorrect="off"
                id="phoneNumber"
                name="phoneNumber"
                className="form-control"
                placeholder={t("enterPhoneNumber")}
                type="text"
                onBlur={form.handleBlur}
                value={form.values.phoneNumber || ""}
                invalid={
                  form.touched.phoneNumber && form.errors.phoneNumber
                    ? true
                    : false
                }
                maxLength={12}
                onChange={(e) => {
                  e.preventDefault();
                  const { value } = e.target;
                  const regex =
                    /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                  if (regex.test(value.toString())) {
                    form.setValues({
                      ...form.values,
                      phoneNumber: value,
                    });
                  } else {
                    form.setValues({
                      ...form.values,
                      phoneNumber: "",
                    });
                  }
                }}
              />
              {form.touched.phoneNumber && form.errors.phoneNumber ? (
                <FormFeedback type="invalid">
                  {form.errors.phoneNumber as string}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label className="form-label" htmlFor="password-input">
                {t("password")}
              </Label>
              {!id && <span className="text-danger">*</span>}
              <div className="position-relative auth-pass-inputgroup mb-3">
                <Input
                  id="password-input"
                  name="password"
                  value={form.values.password || ""}
                  type={passwordType}
                  className="form-control"
                  placeholder={t("enterPassword")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  invalid={
                    form.touched.password && form.errors.password ? true : false
                  }
                  maxLength={25}
                />
                {form.touched.password && form.errors.password ? (
                  <FormFeedback type="invalid">
                    {form.errors.password as string}
                  </FormFeedback>
                ) : null}
                <button
                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                  type="button"
                  id="password-addon"
                >
                  <i
                    className="ri-eye-fill align-middle"
                    onClick={handleClickShowPassword}
                  ></i>
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">{t("gender")}</label>
              <div>
                <div className="form-check form-check-inline">
                  <Input
                    className="form-check-input"
                    type="radio"
                    name="inlineRadioOptions"
                    id="systemAdminInlineRadio1"
                    value={GenderEnum.MALE}
                    checked={form.values.gender === GenderEnum.MALE}
                    onChange={() => {
                      form.setFieldValue("gender", GenderEnum.MALE);
                    }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="systemAdminInlineRadio1"
                  >
                    {t("male")}
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <Input
                    className="form-check-input"
                    type="radio"
                    name="inlineRadioOptions"
                    id="systemAdminInlineRadio2"
                    value={GenderEnum.FEMALE}
                    checked={form.values.gender === GenderEnum.FEMALE}
                    onChange={() => {
                      form.setFieldValue("gender", GenderEnum.FEMALE);
                    }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="systemAdminInlineRadio2"
                  >
                    {t("female")}
                  </label>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <Label className="form-label">
                {t("educationSystems")}
                <span className="text-danger">*</span>
              </Label>
              <EducationSystemsSelect
                isMulti={true}
                closeMenuOnSelect={false}
                value={selectedEducationSystems}
                onChange={onChangeEducationSystems}
                placeholder="selectEducationSystems"
              />
              {form.touched.educationSystemIds &&
              form.errors.educationSystemIds ? (
                <div className="invalid-feedback d-block">
                  {form.errors.educationSystemIds as string}
                </div>
              ) : null}
            </div>
            <div className="mb-3">
              <Label>
                {t("secondaryPhoneNumber")}
                <br />
                <span className="text-info">
                  ({t("ifExistNotificationSentToThisPhoneNumber")})
                </span>
              </Label>

              <PhoneInput
                labels={i18n.language === "ar" ? ar : en}
                locales={["en", "ar"]}
                international
                defaultCountry="KW"
                className="form-control text-light d-flex"
                placeholder="Enter phone number"
                value={form.values.secondaryPhonePhoneNumber}
                onBlur={() => {
                  form.setFieldTouched("secondaryPhonePhoneNumber");
                }}
                onChange={(value: any) => {
                  form.setFieldTouched("secondaryPhonePhoneNumber");
                  form.setFieldValue("secondaryPhonePhoneNumber", value);
                }}
              />
              {form.touched.secondaryPhonePhoneNumber &&
              form.errors.secondaryPhonePhoneNumber ? (
                <FormFeedback type="invalid" className="d-block">
                  {form.errors.secondaryPhonePhoneNumber as string}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={isLoadingAdd || isLoadingUpdate}
              disabled={
                isLoadingAdd ||
                isLoadingUpdate ||
                !!errorSystemAdmin ||
                isLoadingSystemAdmin
              }
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
